import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';

import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerLayout, BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { Text, TextType } from 'ui-components/components/text';

import { UserDateTimezoneFormat } from '../../../components/user-date-timezone-format';
import { useGetCurrentSubscriptionPlan, useGetPendingSubscriptionChangeData } from './hooks/use-plan-details';
import { useGetSimplifiedPlanExperimentName } from './components/plan-name-helper';
import { UNLIMITED_SHIPMENT_LIMIT } from './constants/shipment-limits';
import { ADD_ANALYTICS_LICENSE } from '../../../constants/add-ons';
import Global from '../../../store/global-selectors';

const messages = defineMessages({
  pendingChangeDateLabel: {
    defaultMessage: 'Plan Updating on {date}',
    id: 'NtEX4s',
    description: 'Message for pending subscription change banner regarding the date it will occur',
  },
  pendingChangeDetails: {
    defaultMessage:
      'You are currently on {currentPlanName} with {currentVolume} shipments per month. Your plan will update to {pendingPlanName} with {pendingVolume} shipments per month on your next billing cycle.',
    id: 'ajliqN',
    description: 'Message for pending subscription change banner with plan and shipment volume information',
  },
  pendingAnalyticsLicenseChange: {
    defaultMessage: 'Your additional analytics license count will update from {currentCount} to {pendingCount} on your next billing cycle.',
    id: 'QYHyT5',
    description: 'Message for pending analytics license count change',
  },
  unlimitedShipments: {
    defaultMessage: 'Unlimited',
    id: 'nT5ia8',
    description: 'Label for unlimited shipment volume',
  },
});

const formatShipmentVolume = (limit) => {
  if (limit === UNLIMITED_SHIPMENT_LIMIT) {
    return <FormattedMessage {...messages.unlimitedShipments} />;
  }
  return limit?.toLocaleString() ?? 0;
};

/**
 * Gets the pending shipment limit by checking pendingBillingAddOns first, then falling back to plan default. This is a hacky solution to solve an immediate problem; the whole pending change UI needs some love.
 * @param {BillingAddOn[]} pendingBillingAddOns - Array of pending billing add-ons
 * @param {BillingPlan} pendingPlan - The pending billing plan
 * @returns {number} The pending shipment limit
 */

const getPendingAnalyticsLicenseCount = (pendingBillingAddOns) => {
  const analyticsAddOn = pendingBillingAddOns?.find((addon) => addon.code === ADD_ANALYTICS_LICENSE);
  return analyticsAddOn?.quantity ?? null;
};

const BannerActual = ({
  className = '',
  currentPlan,
  pendingPlan,
  pendingPlanDateTimeUTCString,
  currentShipmentLimit,
  pendingShipmentLimit,
  currentAnalyticsLicenseCount,
  pendingAnalyticsLicenseCount,
  shouldShowShipmentLimitText,
  isAnalyticsLicenseChanging,
}) => {
  const modifiedCurrentPlanName = useGetSimplifiedPlanExperimentName(currentPlan, { shouldShowShipmentLimit: false });
  const modifiedPendingPlanName = useGetSimplifiedPlanExperimentName(pendingPlan, { shouldShowShipmentLimit: false });

  const currentVolume = formatShipmentVolume(currentShipmentLimit);
  const pendingVolume = formatShipmentVolume(pendingShipmentLimit);

  const messageValues = {
    currentPlanName: modifiedCurrentPlanName,
    pendingPlanName: modifiedPendingPlanName,
    currentVolume,
    pendingVolume,
  };

  if (!isAnalyticsLicenseChanging && !shouldShowShipmentLimitText) {
    return null;
  }

  return (
    <BannerMessageV2
      className={className}
      content={
        <>
          {shouldShowShipmentLimitText && (
            <Text formattedMessageValues={messageValues} textType={TextType.Paragraph} value={messages.pendingChangeDetails} />
          )}
          {isAnalyticsLicenseChanging && (
            <Text
              formattedMessageValues={{
                currentCount: currentAnalyticsLicenseCount,
                pendingCount: pendingAnalyticsLicenseCount,
              }}
              textType={TextType.Paragraph}
              value={messages.pendingAnalyticsLicenseChange}
            />
          )}
        </>
      }
      header={
        <Text
          formattedMessageValues={{
            date: <UserDateTimezoneFormat dateTime={pendingPlanDateTimeUTCString} />,
          }}
          textType={TextType.Label}
          value={messages.pendingChangeDateLabel}
        />
      }
      layout={BannerLayout.Full}
      status={BannerStatus.Info}
    />
  );
};

BannerActual.propTypes = {
  className: PropTypes.string,
  currentPlan: PropTypes.object.isRequired,
  pendingPlan: PropTypes.object.isRequired,
  pendingPlanDateTimeUTCString: PropTypes.string.isRequired,
  currentShipmentLimit: PropTypes.number.isRequired,
  pendingShipmentLimit: PropTypes.number.isRequired,
  currentAnalyticsLicenseCount: PropTypes.number.isRequired,
  pendingAnalyticsLicenseCount: PropTypes.number,
  shouldShowShipmentLimitText: PropTypes.bool.isRequired,
  isAnalyticsLicenseChanging: PropTypes.bool.isRequired,
};

export const PendingSubscriptionChangeBanner = ({ className = '' }) => {
  const pendingPlanData = useGetPendingSubscriptionChangeData();
  const currentPlan = useGetCurrentSubscriptionPlan();
  const subscriptionData = useSelector(Global.selectSellerSubscriptionData);

  if (!pendingPlanData) {
    return null;
  }

  const pendingPlanCode = pendingPlanData.plan.code;
  const currentPlanCode = currentPlan.code;
  const isPendingPlanChangeToSamePlanLevel = currentPlanCode.toLowerCase() === pendingPlanCode.toLowerCase();

  const { currentSubscription } = subscriptionData;

  const currentShipmentLimit = currentPlan?.shipmentLimit?.limit ?? 0;
  const { pendingShipmentLimit } = pendingPlanData;
  const isShipmentLimitChanging = currentShipmentLimit !== pendingShipmentLimit;

  const currentAnalyticsLicenseCount = subscriptionData?.currentBillingPlan?.additionalAnalyticsLicenses ?? 0;
  const pendingAnalyticsLicenseCount = getPendingAnalyticsLicenseCount(currentSubscription?.pendingBillingAddOns);
  const isAnalyticsLicenseChanging = pendingAnalyticsLicenseCount !== null && pendingAnalyticsLicenseCount !== currentAnalyticsLicenseCount;

  // If losing byoa access but maintaining same plan level AND no volume/license change
  if (pendingPlanData.isLosingByoaAccessOnly && !isShipmentLimitChanging && !isAnalyticsLicenseChanging) {
    return null;
  }

  // Hide banner only if plan, shipment limit, and analytics licenses are all staying the same
  if (isPendingPlanChangeToSamePlanLevel && !isShipmentLimitChanging && !isAnalyticsLicenseChanging) {
    return null;
  }

  return (
    <BannerActual
      className={className}
      currentAnalyticsLicenseCount={currentAnalyticsLicenseCount}
      currentPlan={currentPlan}
      currentShipmentLimit={currentShipmentLimit}
      isAnalyticsLicenseChanging={isAnalyticsLicenseChanging}
      pendingAnalyticsLicenseCount={pendingAnalyticsLicenseCount}
      pendingPlan={pendingPlanData.plan}
      pendingPlanDateTimeUTCString={pendingPlanData.dateTimeUtc}
      pendingShipmentLimit={pendingShipmentLimit}
      shouldShowShipmentLimitText={!isPendingPlanChangeToSamePlanLevel || isShipmentLimitChanging}
    />
  );
};

PendingSubscriptionChangeBanner.propTypes = {
  className: PropTypes.string,
};
